import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { StatusPill } from '../components/StatusPill';
import { ActiveJobBanner, RunAuditButton } from '../components/AuditJob';
import { BatchNotices } from '../components/BatchNotices';
import { api, ApiError } from '../lib/api';
import { FLAG_TYPES, formatRelative, formatCentsShort, runPrefixFrom, payerColor, type Case, type CaseState } from '../data/sampleData';

interface UserRow { id: number; name: string | null; email: string; role: string; status: string }

// Multi-select toolbar that appears above the table when rows are checked.
// Each action loops the selected case ids through the existing single-case
// endpoints rather than introducing new bulk endpoints — keeps the action
// log + state-machine guards consistent with single-case actions.
function BulkActionBar({
  selectedIds, selectedCases, onClear, onChanged,
}: {
  selectedIds: string[];
  selectedCases: Case[];
  onClear: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sentSome, setSentSome] = useState(false);

  useEffect(() => {
    if (!assignOpen || users) return;
    api<{ users: UserRow[] }>('/api/admin/users')
      .then((d) => setUsers(d.users.filter((u) => u.status === 'active')
        // Decision 0007: FBCs are assignable too, listed after admins and reviewers.
        .sort((a, b) => Number(a.role === 'fbc') - Number(b.role === 'fbc') || (a.name ?? a.email).localeCompare(b.name ?? b.email))))
      .catch(() => setUsers([]));
  }, [assignOpen, users]);

  const numericIds = selectedIds.map((id) => id.replace(/^CASE-/, '').replace(/^0+/, ''));

  const transitionAll = async (toState: 'SURFACED' | 'ACKD' | 'RESOLVED' | 'DISMISSED', label: string) => {
    if (toState === 'ACKD') {
      // Bettina, 2026-11-03: a franchisee answers one notice for many cases,
      // so acknowledge them together. Only Notified cases can move.
      const notified = selectedCases.filter((c) => c.state === 'NOTIFIED').length;
      const other = selectedIds.length - notified;
      if (notified === 0) { setError('None of the selected cases is Notified, so none can be marked acknowledged.'); return; }
      if (!confirm(`Mark ${notified} Notified case${notified === 1 ? '' : 's'} acknowledged?${other > 0 ? `\n\n${other} selected case${other === 1 ? ' is' : 's are'} not Notified and will be skipped.` : ''}`)) return;
    } else if (!confirm(`${label} ${selectedIds.length} case${selectedIds.length === 1 ? '' : 's'}?`)) return;
    const reason = prompt(toState === 'ACKD'
      ? 'Note for the case history, e.g. who responded and when (optional):'
      : `Reason for ${label.toLowerCase()} (optional):`) ?? '';
    setBusy(label);
    setError(null);
    let ok = 0;
    let skipped = 0;
    let failed = 0;
    for (const id of numericIds) {
      try {
        await api(`/api/cases/${id}/transition`, {
          method: 'POST',
          body: { to_state: toState, reason: reason || undefined },
        });
        ok++;
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) skipped++;     // illegal transition for that state
        else failed++;
      }
    }
    setBusy(null);
    if (failed > 0) setError(`${ok} ${label.toLowerCase()}, ${skipped} skipped (state transition not allowed), ${failed} failed.`);
    else if (skipped > 0) setError(`${ok} ${label.toLowerCase()}, ${skipped} skipped (state transition not allowed).`);
    if (ok > 0) onChanged();
  };

  const suppressAll = async () => {
    const reason = prompt(`Suppress ${selectedIds.length} triple${selectedIds.length === 1 ? '' : 's'} permanently?\n\nThis closes each case AND writes a suppression record so the (client × franchisee-stem × zip) triple won't auto-flag in future audits. Reason is required:`);
    if (reason == null) return;
    if (!reason.trim()) { setError('Reason is required to suppress.'); return; }
    setBusy('Suppress');
    setError(null);
    let ok = 0;
    let failed = 0;
    for (const id of numericIds) {
      try {
        await api(`/api/cases/${id}/suppress`, { method: 'POST', body: { reason } });
        ok++;
      } catch { failed++; }
    }
    setBusy(null);
    if (failed > 0) setError(`${ok} suppressed, ${failed} failed.`);
    if (ok > 0) onChanged();
  };

  // Not real clients (Bettina, 2026-10-21): close + suppress in every zip for
  // the servicing franchisee, in one call for all selected cases.
  const notClientAll = async () => {
    const note = prompt(`Mark ${selectedIds.length} case${selectedIds.length === 1 ? '' : 's'} as NOT a real client?\n\nEach is closed and suppressed for its servicing franchisee in every zip, so future audits won't alert on it. Optional note:`);
    if (note === null) return;
    setBusy('Not a client');
    setError(null);
    try {
      await api('/api/cases/not-a-client', { method: 'POST', body: { ids: numericIds.map(Number), note: note.trim() || null } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  };

  // The opposite ruling (2026-11-02): the client IS real — drop the flag and
  // stop audits raising it again for that client.
  const realClientAll = async () => {
    const note = prompt(`Mark ${selectedIds.length} case${selectedIds.length === 1 ? '' : 's'} as a REAL client?\n\nThe "Possible non-client" flag is removed, and future audits stop raising it for these clients. The cases stay open. Optional note:`);
    if (note === null) return;
    setBusy('Real client');
    setError(null);
    try {
      await api('/api/cases/real-client', { method: 'POST', body: { ids: numericIds.map(Number), note: note.trim() || null } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  };

  const assignAll = async (userId: number | null) => {
    setBusy('Assign');
    setError(null);
    setAssignOpen(false);
    let ok = 0;
    let failed = 0;
    for (const id of numericIds) {
      try {
        await api(`/api/cases/${id}/assign`, { method: 'PUT', body: { user_id: userId } });
        ok++;
      } catch { failed++; }
    }
    setBusy(null);
    if (failed > 0) setError(`${ok} assigned, ${failed} failed.`);
    if (ok > 0) onChanged();
  };

  return (
    <div style={{ marginTop: 12, padding: '8px 12px', background: 'color-mix(in oklch, var(--accent) 8%, var(--surface))', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, position: 'relative' }}>
      <span style={{ fontWeight: 500 }}>{selectedIds.length} selected</span>
      <div className="divider-v" style={{ height: 18 }} />

      <div style={{ position: 'relative' }}>
        <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => setAssignOpen((o) => !o)}>
          <Icon name="user" size={12} /> {busy === 'Assign' ? 'Assigning…' : 'Assign…'}
        </button>
        {assignOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            minWidth: 240, padding: 4, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            {!users && <div className="muted" style={{ padding: 10, fontSize: 12 }}>Loading…</div>}
            <div onClick={() => assignAll(null)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Unassign all</div>
            {users?.map((u) => (
              <div key={u.id} onClick={() => assignAll(u.id)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5 }}>
                <div>{u.name ?? u.email}</div>
                <div className="faint" style={{ fontSize: 11 }}>{u.email} · {u.role === 'fbc' ? 'FBC' : u.role}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => transitionAll('SURFACED', 'Mark reviewed')}
              title="Reviewed — ready to notify. Not a confirmed violation.">
        <Icon name="check" size={12} /> {busy === 'Mark reviewed' ? 'Saving…' : 'Mark reviewed'}
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => transitionAll('ACKD', 'Mark acknowledged')}
              title="The franchisee responded to the notice. Notified cases only; others are skipped.">
        <Icon name="check" size={12} /> {busy === 'Mark acknowledged' ? 'Saving…' : 'Mark acknowledged'}
      </button>
      <button className="btn btn-sm btn-danger" disabled={busy != null} onClick={() => setSendOpen(true)}
              title="One notice per franchisee per role for the selected Reviewed cases (decision 0006)">
        <Icon name="bell" size={12} /> Send notices
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => transitionAll('RESOLVED', 'Mark resolved')}>
        <Icon name="check" size={12} /> {busy === 'Mark resolved' ? 'Resolving…' : 'Mark resolved'}
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => transitionAll('DISMISSED', 'Dismiss')} title="Close as one-time false positive (next audit may re-open if conditions persist)">
        <Icon name="close" size={12} /> {busy === 'Dismiss' ? 'Dismissing…' : 'Dismiss'}
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={suppressAll} title="Close + permanently suppress this triple in future audits (reason required)">
        <Icon name="shield" size={12} /> {busy === 'Suppress' ? 'Suppressing…' : 'Suppress'}
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={notClientAll} title="Close + suppress in every zip for the servicing franchisee: the client is an office / training / caregiver record">
        <Icon name="user" size={12} /> {busy === 'Not a client' ? 'Saving…' : 'Not real clients'}
      </button>
      <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={realClientAll} title="These are real clients: remove the Possible non-client flag and stop audits raising it">
        <Icon name="check" size={12} /> {busy === 'Real client' ? 'Saving…' : 'Real clients'}
      </button>
      <button className="btn btn-sm btn-ghost" onClick={() => exportCasesToXlsx(selectedCases)}>
        <Icon name="download" size={12} /> Export selected
      </button>
      {sendOpen && (
        <BatchNotices
          caseIds={numericIds.map(Number)}
          onSent={() => setSentSome(true)}
          onClose={() => { setSendOpen(false); if (sentSome) onChanged(); }}
        />
      )}
      <div style={{ flex: 1 }} />
      {error && <span className="c-warn" style={{ fontSize: 12 }}>{error}</span>}
      <button className="btn btn-sm btn-ghost" onClick={onClear}>Clear</button>
    </div>
  );
}

// xlsx export of the chosen cases. Server-side render at /api/cases/export.xlsx
// matches Bettina's "MD Audit Summary" column shape (Name / Status / Revenue
// / Start of Care / Effective End / Residential Zip / % in encroached zip)
// then appends our extra audit metrics. We POST nothing — just GET with the
// case ids in the query string and let the browser handle the file download.
async function exportCasesToXlsx(cases: Case[]): Promise<void> {
  if (cases.length === 0) { alert('No cases selected to export.'); return; }
  const ids = cases.map((c) => c.id.replace(/^CASE-/, '').replace(/^0+/, '')).join(',');
  const url = `/api/cases/export.xlsx?ids=${encodeURIComponent(ids)}`;
  // Fetch with credentials so the auth cookie carries; serve as blob to force download.
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    alert(`Export failed: ${body?.error ?? res.status}`);
    return;
  }
  const blob = await res.blob();
  const dlUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = dlUrl;
  a.download = `${runPrefixFrom(res)}territory-audit-cases-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(dlUrl);
}

export interface AuditRunFilter {
  id: number;
  label: string;       // e.g. "AF-135, AF-239" or "CA, IL" — scope summary for the banner
}

interface CaseListProps {
  cases: Case[];
  /** Server-side total for the current query (can exceed cases.length under the 500-row cap). */
  totalCases: number;
  onOpen: (id: string) => void;
  onTriggerAudit: () => void;
  /** When set, the queue is server-scoped to this audit run's cases. */
  auditRunFilter: AuditRunFilter | null;
  onSetAuditRunFilter: (f: AuditRunFilter | null) => void;
}

type SortKey = 'created' | 'billed' | 'payer' | 'flags' | 'patient' | 'servicing' | 'owner' | 'zip' | 'state' | 'assignee' | 'longestGap';

// Largest share of the case's revenue (payerSplit is ordered largest first) — the Payer split column sorts on it.
function primaryPayer(c: Case): { name: string; pct: number } | null {
  const first = Object.entries(c.payerSplit)[0];
  return first ? { name: first[0], pct: first[1] } : null;
}

// Minimal slice of GET /api/audit-runs used by the run picker.
interface ApiAuditRun {
  id: number;
  status: string;
  scope_states: string[] | null;
  scope_af_numbers: string[] | null;
  sites_included: string[] | null;
  cases_opened: number | null;
  completed_at: string | null;
  created_at: string;
  triggered_by: { user_id: number; email: string | null; name: string | null };
}

interface PickerRun {
  id: number;
  scope: string;
  casesOpened: number;
  when: string;
  trigger: string;
  isNightly: boolean;
  sitesIncluded: string[] | null;   // null = not recorded for this run
}

function toPickerRun(r: ApiAuditRun): PickerRun {
  const scope = r.scope_af_numbers && r.scope_af_numbers.length > 0
    ? r.scope_af_numbers.join(', ')
    : (r.scope_states ?? []).join(', ') || 'all';
  const trigger = r.triggered_by?.name ?? r.triggered_by?.email ?? `User #${r.triggered_by?.user_id ?? '?'}`;
  const iso = r.completed_at ?? r.created_at;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  const when = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
  return {
    id: r.id,
    scope,
    casesOpened: r.cases_opened ?? 0,
    when,
    trigger,
    sitesIncluded: r.sites_included ?? null,
    // The scheduler runs under the "Nightly Scheduler" system user; those runs
    // are the noise Bettina wants out of the way, so the picker sorts them last.
    isNightly: r.triggered_by?.email === 'system@afi-encroachment.local' || r.triggered_by?.name === 'Nightly Scheduler',
  };
}

function SegmentedControl({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { id: string; label: string; dot?: string }[];
}) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 2, gap: 0 }}>
      {options.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className="btn btn-sm"
            style={{
              border: 'none',
              background: active ? 'var(--surface)' : 'transparent',
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
              fontWeight: active ? 500 : 400,
              color: active ? 'var(--text)' : 'var(--text-muted)',
              padding: '0 10px',
              height: 24,
            }}
          >
            {o.dot && <span className={`pill-dot dot-${o.dot}`}></span>}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** States that mean the case is finished — the Cleared view. */
const CLEARED_STATES: CaseState[] = ['DISMISSED', 'RESOLVED', 'CLEARED_BY_EXCEPTION'];

export function CaseList({ cases, totalCases, onOpen, onTriggerAudit, auditRunFilter, onSetAuditRunFilter }: CaseListProps) {
  // Default to the Priority view (violation candidates + split/cross-zip):
  // encroachment signal first, the data-hygiene QM wall one click away.
  const [sevFilter, setSevFilter] = useState('priority');
  // Under a run scope, the reviewer wants that run's FULL result set (all
  // states, all time) — the run already bounds the data, so the open-only and
  // 90-day defaults would silently hide part of it.
  const [statusFilter, setStatusFilter] = useState(auditRunFilter ? 'all' : 'open');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'created', dir: 'desc' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [jobRefresh] = useState(0);
  // Filters wired up from the dead chip row. windowDays filters by the case's latest audit run date;
  // assigneeFilter filters by case.assignee; afFilter is a substring match on
  // franchisee_af (so "AF-126" matches AF-126, AF-126.2, AF-126.3 etc.);
  // locationFilter is a substring match on the client's "City, ST".
  const [windowDays, setWindowDays] = useState<number>(auditRunFilter ? 0 : 90);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('any');   // 'any' | 'unassigned' | userId-as-string
  const [afFilter, setAfFilter] = useState<string>('');
  // Which side of the case the AF# filter matches. 'servicing' and 'owner'
  // match the whole franchisee by AF stem (AF-126 = AF-126, .2, .3), so
  // "every case serviced by this franchisee" is one filter (Bettina, 2026-11-03).
  const [afRole, setAfRole] = useState<'either' | 'servicing' | 'owner'>('either');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [windowOpen, setWindowOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [runPickerOpen, setRunPickerOpen] = useState(false);
  const [runs, setRuns] = useState<PickerRun[] | null>(null);
  // Nothing is listed until a run is chosen, so it is always clear what the
  // table shows (Bettina, 2026-10-20). "All cases" is an explicit choice.
  const [showAll, setShowAll] = useState(false);
  const noScope = !auditRunFilter && !showAll;
  const [users, setUsers] = useState<{ id: number; name: string | null; email: string }[] | null>(null);
  useEffect(() => {
    if (runs) return;
    api<{ runs: ApiAuditRun[] }>('/api/audit-runs?limit=100')
      .then((d) => setRuns(d.runs.filter((r) => r.status === 'complete').map(toPickerRun)))
      .catch(() => setRuns([]));
  }, [runs]);
  const activeRun = auditRunFilter ? runs?.find((r) => r.id === auditRunFilter.id) ?? null : null;
  const applyRunFilter = (f: AuditRunFilter | null) => {
    onSetAuditRunFilter(f);
    setShowAll(!f);
    setRunPickerOpen(false);
    setSelected(new Set());
    if (f) { setStatusFilter('all'); setWindowDays(0); setSevFilter('all'); }
    else { setStatusFilter('open'); setWindowDays(90); setSevFilter('priority'); }
  };
  // The needs-action banner's "Show" (Bettina, 2026-11-03): every open case
  // with that flag, so the list matches the banner's count.
  const showFlagged = (sev: 'nonclient' | 'spcreview') => {
    if (noScope) setShowAll(true);
    setSevFilter(sev); setStatusFilter('open'); setWindowDays(0);
    setAssigneeFilter('any'); setAfFilter(''); setLocationFilter('');
    setSelected(new Set());
  };
  useEffect(() => {
    if (users || !assigneeOpen) return;
    api<{ users: { id: number; name: string | null; email: string; status: string }[] }>('/api/admin/users')
      .then((d) => setUsers(d.users.filter((u) => u.status === 'active')))
      .catch(() => setUsers([]));
  }, [assigneeOpen, users]);

  // Severity tabs count only cases still in play, so a tab's number always
  // matches the list it opens.
  const scopedCases = noScope ? [] : cases;   // counts read 0 until a run is chosen
  const openOnly = useMemo(() => scopedCases.filter(c => !CLEARED_STATES.includes(c.state)), [scopedCases]);

  const filtered = useMemo(() => {
    if (noScope) return [];   // nothing until a run (or All cases) is chosen
    let arr = [...cases];
    // "Cleared" is a state view, not a severity one: a case here is finished —
    // you dismissed it, you resolved it, or an SPC/IR answered it. It therefore
    // sets its own state set and ignores the open-only status filter, which
    // would otherwise hide every one of them.
    if (sevFilter === 'cleared') {
      arr = arr.filter(c => CLEARED_STATES.includes(c.state));
    } else {
      // A case appears in exactly one tier. Once it is cleared — dismissed,
      // resolved, or answered by an SPC/IR — it leaves the severity view it
      // sat in while it was open, rather than lingering there under the flags
      // it had at the time.
      if (sevFilter !== 'all') arr = arr.filter(c => !CLEARED_STATES.includes(c.state));
      if (sevFilter === 'priority') arr = arr.filter(c => c.sev === 'danger' || c.sev === 'warn');
      else if (sevFilter === 'nonclient') arr = arr.filter(c => c.flags.includes('placeholder_client'));
      else if (sevFilter === 'spcreview') arr = arr.filter(c => c.flags.includes('spc_scope_review'));
      else if (sevFilter !== 'all') arr = arr.filter(c => c.sev === sevFilter);
      if (statusFilter === 'open') arr = arr.filter(c => ['NEW', 'SURFACED', 'NOTIFIED', 'ACKD', 'CONFIRMED_VIOLATION'].includes(c.state));
      else if (statusFilter !== 'all') arr = arr.filter(c => c.state === statusFilter);
    }
    if (windowDays > 0) {
      const cutoff = Date.now() - windowDays * 86_400_000;
      // By the case's latest audit run, not when it first opened (a case opened
      // in August but re-checked last week belongs to "last 30 days").
      arr = arr.filter(c => (c.auditRunAt ?? c.openedAt).getTime() >= cutoff);
    }
    if (assigneeFilter === 'unassigned') {
      arr = arr.filter(c => !c.assignee);
    } else if (assigneeFilter !== 'any') {
      const uid = Number(assigneeFilter);
      arr = arr.filter(c => c.assignee?.id === uid);
    }
    if (afFilter.trim()) {
      const q = afFilter.trim().toUpperCase();
      const stem = afStem(q);
      if (afRole === 'servicing') arr = arr.filter(c => afStem(c.franchiseeAf.toUpperCase()) === stem);
      else if (afRole === 'owner') arr = arr.filter(c => !!c.ownerAf && afStem(c.ownerAf.toUpperCase()) === stem);
      else arr = arr.filter(c => c.franchiseeAf.toUpperCase().includes(q) || (c.ownerAf?.toUpperCase().includes(q) ?? false));
    }
    if (locationFilter.trim()) {
      const q = locationFilter.trim().toLowerCase();
      arr = arr.filter(c => c.location.toLowerCase().includes(q));
    }

    // Numeric ordering for state pills so the visual severity matches the sort.
    const STATE_ORDER: Record<string, number> = {
      NEW: 0, SURFACED: 1, NOTIFIED: 2, ACKD: 3, CONFIRMED_VIOLATION: 4, RESOLVED: 5, DISMISSED: 6,
    };
    arr.sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const k = sort.key;
      if (k === 'created')   return (a.openedAt.getTime() - b.openedAt.getTime()) * dir;
      if (k === 'billed')    return (a.totalRevenueCents - b.totalRevenueCents) * dir;
      if (k === 'payer')     return (primaryPayer(a)?.name ?? '~~~').localeCompare(primaryPayer(b)?.name ?? '~~~') * dir;
      if (k === 'flags')     return (a.flags.length - b.flags.length) * dir;
      if (k === 'patient')   return a.patient.localeCompare(b.patient) * dir;
      if (k === 'servicing') return a.franchiseeAf.localeCompare(b.franchiseeAf) * dir;
      if (k === 'owner')     return (a.ownerAf ?? '').localeCompare(b.ownerAf ?? '') * dir;
      if (k === 'zip')       return (a.dominantZip ?? '').localeCompare(b.dominantZip ?? '') * dir;
      if (k === 'state')     return ((STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99)) * dir;
      if (k === 'assignee')  return ((a.assignee?.name ?? a.assignee?.email ?? '~~~').localeCompare(b.assignee?.name ?? b.assignee?.email ?? '~~~')) * dir;
      if (k === 'longestGap') return ((a.longestGapDays ?? -1) - (b.longestGapDays ?? -1)) * dir;
      return 0;
    });
    return arr;
    // Every filter the memo body reads MUST be in this list — a missing dep
    // serves the stale cached result and the filter silently "does nothing"
    // (the 7/1 legal-meeting bug: afFilter/windowDays/assigneeFilter were absent).
  }, [cases, noScope, sevFilter, statusFilter, windowDays, assigneeFilter, afFilter, afRole, locationFilter, sort]);

  const toggleSel = (id: string) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.id)));
  };

  const sortHdr = (key: SortKey, label: string, align?: 'right') => {
    const active = sort.key === key;
    return (
      <th className="sortable" style={{ textAlign: align ?? 'left' }} onClick={() => setSort({ key, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: active ? 'var(--text)' : undefined }}>
          {label}
          {active && <Icon name={sort.dir === 'desc' ? 'arrowdown' : 'arrowup'} size={11} />}
        </span>
      </th>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '20px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Manual review queue</div>
            <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Case queue</h1>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <RunAuditButton onClick={onTriggerAudit} />
            <button className="btn" onClick={() => exportCasesToXlsx(filtered)} title="Download all cases matching the current filters as Excel">
              <Icon name="download" size={13} /> Export all
            </button>
          </div>
        </div>
        <ActiveJobBanner refreshKey={jobRefresh} />
        <NeedsActionBanner cases={cases} inRun={!!auditRunFilter} onShow={showFlagged} />

        {auditRunFilter && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <Icon name="filter" size={13} />
            <span>
              Viewing results of <span className="mono" style={{ fontWeight: 600 }}>RUN-{auditRunFilter.id}</span>
              {auditRunFilter.label && <span className="muted"> · {auditRunFilter.label}</span>}
              <span className="muted"> · {totalCases} case{totalCases === 1 ? '' : 's'}</span>
              {totalCases > cases.length && (
                <span className="c-warn"> · showing first {cases.length} of {totalCases}</span>
              )}
            </span>
            <div style={{ flex: 1 }} />
            <button className="btn btn-sm" onClick={() => applyRunFilter(null)}>Show all cases</button>
          </div>
        )}
        {auditRunFilter && activeRun && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.6 }}>
            <strong>Territories included{activeRun.sitesIncluded ? ` (${activeRun.sitesIncluded.length} sites)` : ''}:</strong>{' '}
            {activeRun.sitesIncluded ? <span className="mono">{activeRun.sitesIncluded.join(', ')}</span> : 'not recorded for this run (it predates 20 Oct 2026)'}
          </div>
        )}

        {/* Filter by audit run — the first choice on this screen (Bettina, 2026-10-20). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Filter by audit run</span>
          {/* Audit-run scope picker — "choose the audit you just ran, these are
              the results" (Bettina, 7/1). Manual/triggered runs list first;
              nightly scheduler runs sort last. */}
          <div style={{ position: 'relative' }}>
            <button className="btn" onClick={() => setRunPickerOpen(o => !o)}
                    style={{ minWidth: 300, justifyContent: 'space-between', height: 36, fontSize: 13.5, border: '2px solid var(--accent)', background: auditRunFilter || showAll ? 'var(--accent-soft)' : 'var(--surface)', fontWeight: 600 }}>
              <span>
                {auditRunFilter ? <>RUN-{auditRunFilter.id}{auditRunFilter.label ? <span className="muted" style={{ fontWeight: 400 }}> · {auditRunFilter.label}</span> : null}</>
                  : showAll ? 'All cases (every run)' : 'Choose an audit run…'}
              </span>
              <Icon name="chevdown" size={13} />
            </button>
            {runPickerOpen && (
              <>
                <div onClick={() => setRunPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 340, maxHeight: 380, overflow: 'auto', padding: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  <div onClick={() => applyRunFilter(null)} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: !auditRunFilter ? 'var(--accent-soft)' : undefined, borderRadius: 3, borderBottom: '1px solid var(--border)' }}>
                    All cases (every run)
                  </div>
                  {!runs && <div className="muted" style={{ padding: 8, fontSize: 12 }}>Loading runs…</div>}
                  {runs && runs.length === 0 && <div className="muted" style={{ padding: 8, fontSize: 12 }}>No completed runs yet.</div>}
                  {runs && [...runs].sort((a, b) => (a.isNightly === b.isNightly ? b.id - a.id : a.isNightly ? 1 : -1)).map((r) => (
                    <div key={r.id} onClick={() => applyRunFilter({ id: r.id, label: r.scope })} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: auditRunFilter?.id === r.id ? 'var(--accent-soft)' : undefined, borderRadius: 3 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="mono" style={{ fontWeight: 600 }}>RUN-{r.id}</span>
                        <span>· {r.scope}</span>
                        {r.isNightly && <span className="faint" style={{ fontSize: 10, padding: '1px 4px', background: 'var(--surface-2)', borderRadius: 3 }}>nightly</span>}
                      </div>
                      <div className="faint" style={{ fontSize: 11 }}>{r.casesOpened} case{r.casesOpened === 1 ? '' : 's'} · {r.trigger} · {r.when}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {activeRun && (
            <span className="muted" style={{ fontSize: 12 }}>{activeRun.casesOpened} case{activeRun.casesOpened === 1 ? '' : 's'} · {activeRun.trigger} · {activeRun.when}</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <SegmentedControl
            value={sevFilter} onChange={setSevFilter}
            options={[
              { id: 'priority', label: `Priority · ${openOnly.filter(c => c.sev === 'danger' || c.sev === 'warn').length}`, dot: 'danger' },
              { id: 'all', label: `All · ${scopedCases.length}` },
              { id: 'danger', label: `Critical · ${openOnly.filter(c => c.sev === 'danger').length}`, dot: 'danger' },
              { id: 'warn', label: `Warn · ${openOnly.filter(c => c.sev === 'warn').length}`, dot: 'warn' },
              { id: 'gold', label: `Caution · ${openOnly.filter(c => c.sev === 'gold').length}`, dot: 'gold' },
              { id: 'cleared', label: `Cleared · ${scopedCases.filter(c => CLEARED_STATES.includes(c.state)).length}`, dot: 'ok' },
              { id: 'nonclient', label: `Possible non-clients · ${openOnly.filter(c => c.flags.includes('placeholder_client')).length}`, dot: 'warn' },
              { id: 'spcreview', label: `SPC needs review · ${openOnly.filter(c => c.flags.includes('spc_scope_review')).length}`, dot: 'warn' },
            ]}
          />
          <div className="divider-v" style={{ height: 24 }} />
          <select className="select" style={{ width: 'auto', height: 30 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="open">Open (New → Confirmed violation)</option>
            <option value="all">Any state</option>
            <option value="NEW">New</option>
            <option value="SURFACED">Reviewed</option>
            <option value="NOTIFIED">Notified</option>
            <option value="ACKD">Acknowledged</option>
            <option value="CONFIRMED_VIOLATION">Confirmed violation</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </select>

          {/* Audit run timeframe — the case's latest audit run within the last N days */}
          <div style={{ position: 'relative' }}>
            <button className="chip" onClick={() => setWindowOpen(o => !o)} style={{ cursor: 'pointer', border: 'none' }}>
              Filter by audit run timeframe: <strong>{windowDays === 0 ? 'All time' : `Last ${windowDays} days`}</strong> <Icon name="chevdown" size={11} />
            </button>
            {windowOpen && (
              <>
                <div onClick={() => setWindowOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 160, padding: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {[14, 30, 90, 180, 365, 0].map(d => (
                    <div key={d} onClick={() => { setWindowDays(d); setWindowOpen(false); }} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: windowDays === d ? 'var(--accent-soft)' : undefined, borderRadius: 3 }}>
                      {d === 0 ? 'All time' : `Last ${d} days`}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Assignee filter */}
          <div style={{ position: 'relative' }}>
            <button className="chip" onClick={() => setAssigneeOpen(o => !o)} style={{ cursor: 'pointer', border: 'none' }}>
              Assignee: {
                assigneeFilter === 'any' ? 'anyone'
                : assigneeFilter === 'unassigned' ? 'unassigned'
                : (users?.find(u => String(u.id) === assigneeFilter)?.name ?? users?.find(u => String(u.id) === assigneeFilter)?.email ?? 'user')
              } <Icon name="chevdown" size={11} />
            </button>
            {assigneeOpen && (
              <>
                <div onClick={() => setAssigneeOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 220, padding: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  <div onClick={() => { setAssigneeFilter('any'); setAssigneeOpen(false); }} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: assigneeFilter === 'any' ? 'var(--accent-soft)' : undefined, borderRadius: 3 }}>Anyone</div>
                  <div onClick={() => { setAssigneeFilter('unassigned'); setAssigneeOpen(false); }} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: assigneeFilter === 'unassigned' ? 'var(--accent-soft)' : undefined, borderRadius: 3, borderBottom: '1px solid var(--border)' }}>Unassigned</div>
                  {!users && <div className="muted" style={{ padding: 8, fontSize: 12 }}>Loading…</div>}
                  {users?.map(u => (
                    <div key={u.id} onClick={() => { setAssigneeFilter(String(u.id)); setAssigneeOpen(false); }} style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', background: assigneeFilter === String(u.id) ? 'var(--accent-soft)' : undefined, borderRadius: 3 }}>
                      <div>{u.name ?? u.email}</div>
                      <div className="faint" style={{ fontSize: 11 }}>{u.email}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* AF# / location filters — active ones render as removable chips;
              the Add-filter popover offers whichever are unset. */}
          {afFilter && (
            <span className="chip" style={{ cursor: 'default' }}>
              AF: <span className="mono">{afFilter}</span>
              <select value={afRole} onChange={(e) => setAfRole(e.target.value as 'either' | 'servicing' | 'owner')}
                      title="Match this AF# as the servicing franchisee, the territory owner, or either"
                      style={{ marginLeft: 6, fontSize: 11.5, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                <option value="either">servicing or owner</option>
                <option value="servicing">servicing only</option>
                <option value="owner">territory owner only</option>
              </select>{' '}
              <span className="chip-x" style={{ cursor: 'pointer' }} onClick={() => { setAfFilter(''); setAfRole('either'); }}>×</span>
            </span>
          )}
          {locationFilter && (
            <span className="chip" style={{ cursor: 'default' }}>
              Location: {locationFilter}{' '}
              <span className="chip-x" style={{ cursor: 'pointer' }} onClick={() => setLocationFilter('')}>×</span>
            </span>
          )}
          {(!afFilter || !locationFilter) && (
            <div style={{ position: 'relative' }}>
              <button className="chip" onClick={() => setAddFilterOpen(o => !o)} style={{ cursor: 'pointer', border: 'none' }}>
                <Icon name="plus" size={11} /> Add filter
              </button>
              {addFilterOpen && (
                <>
                  <div onClick={() => setAddFilterOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 260, padding: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                    {!afFilter && (
                      <>
                        <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>AF# (servicing or owner)</div>
                        <input
                          type="text" autoFocus className="input"
                          placeholder="AF-186 or 186"
                          style={{ width: '100%', fontSize: 12.5 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const v = (e.target as HTMLInputElement).value.trim();
                              if (v) setAfFilter(v.startsWith('AF-') ? v : `AF-${v}`);
                              setAddFilterOpen(false);
                            }
                            if (e.key === 'Escape') setAddFilterOpen(false);
                          }}
                        />
                      </>
                    )}
                    {!locationFilter && (
                      <>
                        <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: afFilter ? '0 0 6px' : '10px 0 6px' }}>Location (city or state)</div>
                        <input
                          type="text" autoFocus={!!afFilter} className="input"
                          placeholder="Pittsburgh or PA"
                          style={{ width: '100%', fontSize: 12.5 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const v = (e.target as HTMLInputElement).value.trim();
                              if (v) setLocationFilter(v);
                              setAddFilterOpen(false);
                            }
                            if (e.key === 'Escape') setAddFilterOpen(false);
                          }}
                        />
                      </>
                    )}
                    <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>Press Enter to apply.</div>
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 12.5 }}>{filtered.length} of {scopedCases.length}</span>
        </div>

        {selected.size > 0 && (
          <BulkActionBar
            selectedIds={Array.from(selected)}
            selectedCases={filtered.filter((c) => selected.has(c.id))}
            onClear={() => setSelected(new Set())}
            onChanged={() => { setSelected(new Set()); window.location.reload(); }}
          />
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', marginTop: 16, borderTop: '1px solid var(--border)' }}>
        <table className="tbl" style={{ minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} style={{ accentColor: 'var(--accent)' }} />
              </th>
              <th style={{ width: 36 }}></th>
              {sortHdr('servicing', 'Servicing AF#')}
              {sortHdr('owner', 'Owner of Zip')}
              <th style={{ width: 110 }}>Case</th>
              {sortHdr('patient', 'Client · location')}
              {sortHdr('flags', 'Flags')}
              {sortHdr('payer', 'Payer split')}
              {sortHdr('billed', 'Revenue', 'right')}
              {sortHdr('state', 'State')}
              {sortHdr('assignee', 'Assignee')}
              {sortHdr('longestGap', 'Longest gap', 'right')}
              {sortHdr('created', 'Opened')}
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {noScope && (
              <tr><td colSpan={14} style={{ padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Choose an audit run above to see its cases.</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  Or pick <strong>All cases (every run)</strong> in the same list to see everything.
                </div>
              </td></tr>
            )}
            {!noScope && filtered.map(c => {
              const isSel = selected.has(c.id);
              return (
                <tr key={c.id} className={`clickable${isSel ? ' selected' : ''}`} onClick={() => onOpen(c.id)}>
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleSel(c.id)} style={{ accentColor: 'var(--accent)' }} />
                  </td>
                  <td>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: `var(--${c.sev === 'ok' ? 'ok' : c.sev})` }} />
                  </td>
                  <td>
                    <div className="mono" style={{ fontSize: 12.5 }}>{c.franchiseeAf}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{c.franchiseeName}</div>
                  </td>
                  <td>
                    {/* Three distinct cases, previously collapsed into two and
                        mislabelled: nobody owns the zip ("own territory" was
                        wrong — it's unowned), the servicing franchisee owns it
                        themselves (was printing their own AF# back at them),
                        or a different franchisee owns it (real encroachment —
                        the AF# is the point, so it stays). */}
                    {!c.ownerAf ? (
                      <span className="muted" style={{ fontSize: 12 }}>Unowned territory · {c.dominantZip}</span>
                    ) : afStem(c.ownerAf) === afStem(c.franchiseeAf) ? (
                      <span className="muted" style={{ fontSize: 12 }}>Own territory · {c.dominantZip}</span>
                    ) : (
                      <>
                        <div className="mono c-warn" style={{ fontSize: 12.5 }}>{c.ownerAf}</div>
                        <div className="muted" style={{ fontSize: 11 }}>zip {c.dominantZip}</div>
                      </>
                    )}
                  </td>
                  <td className="mono" style={{ fontSize: 12.5, fontWeight: 500 }}>{c.id}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {c.patient}
                      {c.legacy && (
                        <span className="c-ok" style={{ fontSize: 10.5, marginLeft: 6, fontWeight: 600 }}
                              title={`Legacy client — grandfathered: ${c.legacy.reason} No break in service voided it.`}>· Legacy</span>
                      )}
                      {c.clientActive !== true && (c.clientStatus || c.clientActive === false) && (
                        <span className="c-warn" style={{ fontSize: 10.5, marginLeft: 6 }} title={`AxisCare status: ${c.clientActive === false ? 'Inactive' : 'unknown'}${c.clientStatus ? ` · ${c.clientStatus}` : ''}`}>
                          · {c.clientActive === false ? 'Inactive' : 'Status unknown'}{c.clientStatus && !/^inactive$/i.test(c.clientStatus) ? ` · ${c.clientStatus}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      <span className="mono">{c.axiscareClientId}</span> · {c.location}
                      {c.zipOnlyTag && <span className="pill" style={{ marginLeft: 6, height: 16, fontSize: 9.5 }}>ZIP-ONLY</span>}
                    </div>
                  </td>
                  <td>
                    {c.flags.length === 0 ? (
                      <span className="muted" style={{ fontSize: 12 }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {c.flags.slice(0, 3).map(f => {
                          const ft = FLAG_TYPES[f];
                          return <span key={f} className={`pill flag-pill sev-${ft.sev}`}>
                            <span className={`pill-dot dot-${ft.sev}`}></span>{ft.label}
                          </span>;
                        })}
                        {c.flags.length > 3 && <span className="pill"><span className="pill-dot"></span>+{c.flags.length - 3}</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 11.5, minWidth: 150 }}>
                    {/* Mirrors the case screen's Payer split box: colour bar + every payer and its % of revenue, largest first. */}
                    {Object.keys(c.payerSplit).length === 0 ? <span className="muted">—</span> : (
                      <>
                        <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', marginBottom: 3, background: 'var(--surface-2)' }}>
                          {Object.entries(c.payerSplit).map(([n, v]) => (
                            <span key={n} style={{ width: `${Math.max(v, 1)}%`, background: payerColor(c.payerSplit, n) }} />
                          ))}
                        </div>
                        {Object.entries(c.payerSplit).map(([n, v]) => (
                          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1.35 }}>
                            <span style={{ width: 7, height: 7, borderRadius: 2, flexShrink: 0, background: payerColor(c.payerSplit, n) }} />
                            <span style={{ flex: 1 }}>{n}</span>
                            <span className="mono">{v === 0 ? '<1' : v}%</span>
                          </div>
                        ))}
                      </>
                    )}
                  </td>
                  <td className="num mono" style={{ textAlign: 'right' }}>{formatCentsShort(c.totalRevenueCents)}</td>
                  <td><StatusPill state={c.state} /></td>
                  <td>
                    {c.assignee ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div className="avatar" style={{ width: 22, height: 22, fontSize: 10 }}>
                          {(c.assignee.name ?? c.assignee.email ?? '?').split(/\s|@/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <span style={{ fontSize: 12.5 }}>{c.assignee.name ?? c.assignee.email}</span>
                      </div>
                    ) : <span className="muted" style={{ fontSize: 12 }}>Unassigned</span>}
                  </td>
                  <td className="num mono" style={{ textAlign: 'right', fontSize: 12 }}>
                    {c.longestGapDays == null
                      ? <span className="faint">—</span>
                      : <span className={c.longestGapDays >= 30 ? 'c-warn' : ''}>{c.longestGapDays}d</span>}
                  </td>
                  <td className="muted mono" style={{ fontSize: 12 }}>{formatRelative(c.openedAt)}</td>
                  {/* Reserved for a per-row action menu; the old dot3 button had no
                      handler and read as broken. */}
                  <td></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// "AF-101.2" → "AF-101". Sub-territories belong to the same franchisee, so a
// case in AF-101.2's zip serviced by AF-101 is that franchisee's own territory,
// not encroachment.
function afStem(af: string): string {
  return af.replace(/\.\d+$/, '');
}

// Cases that need a decision before anything else can happen with them
// (Bettina, 2026-11-03): a possible non-client (real client or not?) and an
// SPC that may cover the pair. Counts every open case in the loaded set (all
// cases, or the chosen run), whatever the filters below show.
function NeedsActionBanner({ cases, inRun, onShow }: {
  cases: Case[]; inRun: boolean; onShow: (sev: 'nonclient' | 'spcreview') => void;
}) {
  const open = cases.filter(c => !CLEARED_STATES.includes(c.state));
  const preNotice = (c: Case) => c.state === 'NEW' || c.state === 'SURFACED';
  const groups = [
    { sev: 'nonclient' as const, label: 'Possible non-clients', what: 'confirm real client or not', list: open.filter(c => c.flags.includes('placeholder_client')) },
    { sev: 'spcreview' as const, label: 'SPC needs review', what: 'confirm whether the contract covers the client', list: open.filter(c => c.flags.includes('spc_scope_review')) },
  ].filter(g => g.list.length > 0);
  if (!groups.length) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius)', background: 'var(--warn-soft)',
      border: '1px solid color-mix(in oklch, var(--warn) 45%, var(--border))', fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
        <Icon name="alert" size={14} color="var(--warn)" />
        Review these first{inRun ? ' (this run)' : ''}: they need a decision before the case can move forward
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {groups.map(g => {
          const before = g.list.filter(preNotice).length;
          const after = g.list.length - before;
          return (
            <div key={g.sev} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span><strong>{g.list.length}</strong> {g.label}</span>
              <span className="muted" style={{ fontSize: 12 }}>
                {g.what}{after > 0 ? ` · ${before} not yet notified, ${after} already notified` : ''}
              </span>
              <div style={{ flex: 1 }} />
              <button className="btn btn-sm" onClick={() => onShow(g.sev)}>Show {g.list.length}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
